import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { useDndContext, type UniqueIdentifier } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useMemo } from "react";
import { TaskCard } from "./TaskCard";
import { cva } from "class-variance-authority";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { GripVertical } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/ScrollArea"
import {Task} from "@/app/types/tasks";

export interface Column {
  id: UniqueIdentifier;
  title: string;
}

export type ColumnType = "Column";

export interface ColumnDragData {
  type: ColumnType;
  column: Column;
}

interface BoardColumnProps {
  column: Column;
  tasks: Task[];
  isOverlay?: boolean;
  isDraggable?: boolean;
  setOpenLog: (open: boolean) => void;
  setTaskId: (id: number | undefined) => void;
  viewKanban: string;
  onEditTask: (task: Task) => void; // Add this prop
}

export function BoardColumn({ onEditTask,column, tasks, isOverlay,isDraggable,setOpenLog,setTaskId, viewKanban}: BoardColumnProps) {
  const tasksIds = useMemo(() => {
    return tasks.map((task) => task.task_id);
  }, [tasks]);

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: {
      type: "Column",
      column,
    } satisfies ColumnDragData,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
    disabled: !isDraggable, // Disable based on prop
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const variants = cva(
    "h-[500px] max-h-[500px] w-[300px] md:w-[320px] lg:w-[280px] bg-primary-foreground flex flex-col flex-shrink-0 snap-center shadow-md",
    {
      variants: {
        dragging: {
          default: "border-2 border-transparent",
          over: "ring-2 opacity-30",
          overlay: "ring-2 ring-primary",
        },
      },
    }
  );

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={variants({
        dragging: isOverlay ? "overlay" : isDragging ? "over" : undefined,
      })}

    >
      <CardHeader className="p-4 font-semibold border-b-2 text-left flex flex-row justify-between items-center">
        <span className="inline-flex items-center justify-center  text-xl pr-4 md:text-base lg:text-lg ">
          {" "}
          {column.title}
        </span>
        <span className="inline-flex items-center justify-center w-6 h-6 text-sm bg-gray-100 rounded-full">
          {tasks.length}
        </span>
      </CardHeader>
      <ScrollArea className="px-2" hideScrollToTop={true}>
        <CardContent className="flex flex-grow flex-col gap-4 py-4 px-2">
          <SortableContext items={tasksIds}>
            {tasks.map((task) => (
              <TaskCard
                key={task.task_id}
                task={task}
                setOpenLog={setOpenLog}
                setTaskId={setTaskId}
                viewKanban={viewKanban}
                onEditTask={onEditTask}
              />
            ))}
          </SortableContext>
        </CardContent>
      </ScrollArea>
    </Card>
  );
}

export function BoardContainer({ children }: { children: React.ReactNode }) {
  const dndContext = useDndContext();

  const variations = cva(
    "px-2 md:px-0 flex lg:justify-center pb-4 max-w-[1600px] mx-auto", 
    {
      variants: {
        dragging: {
          default: "snap-x snap-mandatory",
          active: "snap-none",
        },
      },
    },
  );

  return (
    <ScrollArea
      className={variations({
        dragging: dndContext.active ? "active" : "default",
      })}
      hideScrollToTop={true}
    >
      <div className="flex gap-2 md:gap-3 lg:gap-4 items-center flex-row justify-start lg:justify-center px-2 md:px-4">
        {children}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
